import { Id, toast } from 'react-toastify';
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import 'react-toastify/dist/ReactToastify.css';
import { Spinner } from '@cimplico/components';

type ToastType = 'error' | 'success';
type ToastContent = { title: string; description?: string | string[] };

type ToastMessageProps = {
    type: ToastType;
} & Partial<ToastContent>;

function ToastMessage({ type, title, description }: ToastMessageProps): JSX.Element {
    return (
        <div className="flex items-start">
            <div className="flex-shrink-0">
                {type === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                ) : (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
                )}
            </div>
            <div className="ml-3 w-0 flex-1 text-sm">
                <p className="font-medium text-gray-900">
                    {title || (type === 'success' ? 'Successfully created!' : 'Something went wrong!')}
                </p>
                {description &&
                    (typeof description === 'string' ? (
                        <p className="mt-1 text-gray-500">{description}</p>
                    ) : (
                        description.map((d, idx) => (
                            <p key={idx} className="mt-1 text-gray-500">
                                {d}
                            </p>
                        ))
                    ))}
            </div>
        </div>
    );
}

const toastStyle = 'h-fit min-h-0 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5';

/**
 * Renders a toast notification.
 *
 * @param type 'success' or 'error'
 * @param title Main message of toast
 * @param description Optional description providing additional context
 */
export function toastNotification(type: ToastType, title?: string, description?: string | string[]): void {
    toast(ToastMessage({ type, title, description }), {
        className: toastStyle,
        type,
        autoClose: type === 'error' ? false : 3500,
        position: 'bottom-center',
        style: { minHeight: 'auto' },
    });
}

export type ToastDoneFunction = (result: ToastType, content: ToastContent) => void;
/**
 * Renders a toast in a loading (pending state) with a spinner.
 *
 * @param message Custom loading message
 * @returns `done` function to be called when the async action has completed. Updates the toast to a success or error state with a custom method.
 * @example
 * ```
 * done = asyncToast('Loading...')
 * someAsyncAction()
 * .then(() => done('success', { title: 'Success!', description: 'You did it!' }))
 * .catch((e) => done('error', { title: 'Error!', description: e.message }))
 * ```
 */
export function asyncToast(message: string): ToastDoneFunction {
    const id = toast.loading(
        <div className="flex items-center h-full w-full gap-4">
            <Spinner size="sm" />
            <label className="text-sm text-gray-600">{message}</label>
        </div>,
        {
            className: toastStyle,
            autoClose: false,
            position: 'bottom-center',
            closeOnClick: false,
            closeButton: false,
        }
    );

    function done(result: ToastType, content: ToastContent): void {
        toast.update(id, {
            render: ToastMessage({ type: result, ...content }),
            type: result,
            isLoading: false,
            autoClose: result === 'error' ? false : 3500,
            closeOnClick: true,
            closeButton: true,
        });
    }

    return done;
}
